/**
 * DetonateSkillEffect — Conjuration Swarm Fork 2 command: self-destruct all wisps.
 *
 * On activation, every living wisp simultaneously self-destructs. Each wisp
 * produces an AoE explosion at its current position:
 *   damage = baseDamage + lifespanBonus
 *   lifespanBonus = clamp(1 - age/maxWispLifespanMs, 0, 1) * maxLifespanBonus
 * Fresh wisps hit hardest; older wisps deal closer to base damage.
 * Wisps are destroyed with deathCause 'detonate' — Expendable and Hair Trigger
 * do NOT fire (their combat-kill guard prevents it).
 *
 * Upgrade hooks (resolved at activation time):
 * - Volatile (detonate_upgrade_1): radiusMultiplier (×1.3) and flatDamageBonus (+4)
 *
 * UpgradeEffectResolver keys (detonate scope):
 * - radiusMultiplier: multiplicative, base 1.0 — Volatile contributes 1.3
 * - flatDamageBonus: additive, base 0 — Volatile contributes 4
 *
 * Long cooldown tier (2000ms). Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Detonate
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

/** Snapshot of a living wisp at detonation time. */
export interface WispData {
  readonly id: string;
  readonly position: { x: number; y: number };
  readonly spawnTimeMs: number;
}

export interface DetonateDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string, base?: number): number;
  };
  readonly summons: {
    /** Returns all currently living wisps with their spawn timestamps. */
    getWisps(): WispData[];
    /** Destroys all living wisps with deathCause: 'detonate'. */
    commandDetonate(): void;
  };
  readonly combat: {
    /** Apply AoE damage at (x, y) within radiusTiles, for the given damage amount. */
    applyAoEDamage(x: number, y: number, radiusTiles: number, damage: number): void;
  };
  readonly clock: {
    /** Current simulation time in ms (used to compute wisp ages). */
    nowMs(): number;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface DetonateConfig {
  readonly cooldownMs: number;
  /** Base damage per wisp explosion (before lifespan bonus). */
  readonly baseDamage: number;
  /** Blast radius in tiles at baseline (before Volatile multiplier). */
  readonly baseRadiusTiles: number;
  /**
   * Maximum lifespan bonus added to damage for a freshly-spawned wisp.
   * A wisp with age 0 gets this full bonus; age >= maxWispLifespanMs gets 0.
   */
  readonly maxLifespanBonus: number;
  /**
   * Reference lifespan scale for the damage curve (ms).
   * Wisps do not actually expire at this age — it is only the scale for
   * computing lifespanBonus.
   */
  readonly maxWispLifespanMs: number;
}

const DEFAULT_CONFIG: DetonateConfig = {
  cooldownMs: 2000,            // long cooldown tier
  baseDamage: 8,               // spec § Detonate base damage
  baseRadiusTiles: 1.5,        // spec § Detonate blast radius
  maxLifespanBonus: 12,        // spec § tentatively 12 (range 8–20)
  maxWispLifespanMs: 30000,    // reference lifespan scale: 30 seconds
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class DetonateSkillEffect implements SkillEffect {
  readonly skillId = 'detonate';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: DetonateDeps;
  private readonly config: DetonateConfig;

  private _cooldownRemaining = 0;

  // Observable state for scenarios and integration layer
  private _detonationCount = 0;
  private _lastWispsDetonated = 0;
  private _lastExplosionDamages: number[] = [];
  private _resolvedRadiusTiles = 0;
  private _resolvedFlatDamageBonus = 0;

  constructor(deps: DetonateDeps, config: Partial<DetonateConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get detonationCount(): number { return this._detonationCount; }
  get lastWispsDetonated(): number { return this._lastWispsDetonated; }
  get lastExplosionDamages(): readonly number[] { return this._lastExplosionDamages; }
  get resolvedRadiusTiles(): number { return this._resolvedRadiusTiles; }
  get resolvedFlatDamageBonus(): number { return this._resolvedFlatDamageBonus; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const wisps = this.deps.summons.getWisps();

    if (wisps.length === 0) {
      // No wisps to detonate — skill is usable but does nothing. Consume cooldown.
      this._detonationCount++;
      this._lastWispsDetonated = 0;
      this._lastExplosionDamages = [];
      this._cooldownRemaining = this.config.cooldownMs;
      return true;
    }

    const now = this.deps.clock.nowMs();

    // Resolve Volatile upgrade parameters (detonate scope)
    const radiusMultiplier = this.deps.resolver.getMultiplicativeEffect(
      'detonate', 'radiusMultiplier', 1.0,
    );
    const flatDamageBonus = this.deps.resolver.getAdditiveEffect('detonate', 'flatDamageBonus');

    const resolvedRadius = this.config.baseRadiusTiles * radiusMultiplier;
    this._resolvedRadiusTiles = resolvedRadius;
    this._resolvedFlatDamageBonus = flatDamageBonus;

    const resolvedBaseDamage = this.config.baseDamage + flatDamageBonus;

    // Detonate each wisp: compute lifespan bonus and apply AoE explosion
    const explosionDamages: number[] = [];

    for (const wisp of wisps) {
      const age = Math.max(0, now - wisp.spawnTimeMs);
      const lifespanRatio = Math.min(1, age / this.config.maxWispLifespanMs);
      const lifespanBonus = Math.round((1 - lifespanRatio) * this.config.maxLifespanBonus);
      const damage = resolvedBaseDamage + lifespanBonus;

      this.deps.combat.applyAoEDamage(
        wisp.position.x,
        wisp.position.y,
        resolvedRadius,
        damage,
      );

      explosionDamages.push(damage);
    }

    // Destroy all wisps with deathCause: 'detonate'
    this.deps.summons.commandDetonate();

    this._detonationCount++;
    this._lastWispsDetonated = wisps.length;
    this._lastExplosionDamages = explosionDamages;
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._lastExplosionDamages = [];
  }
}
